import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { ErrorNote, Loading, formatBytes } from "../../components/Feedback";
import { Badge, Btn, Card, Field, Input } from "../../components/Shared";
import { useApi } from "../../useApi";
import { SuccessNote } from "./parts";

// Management -> Stored files (task 2026-10-05-stored-files-are-checked-against-their-checksums).
// GET /api/admin/object-checks: the rate, a summary and every object whose bytes no longer match
// what the database recorded (or that is gone), with the project and the rows that name it.
// PUT .../settings {objects_per_run, mib_per_run, retention_days}; POST .../run runs one batch now.
// retention_days: superseded and deleted file versions nothing names are purged after it (daily
// job expire-file-versions; 0 = never).

const STATUS_COLOR = { mismatch: "red", missing: "red", error: "amber" };
const STATUS_TEXT = {
  mismatch: "changed",
  missing: "missing",
  error: "not readable",
};

function retentionText(days) {
  return days === 0
    ? "Old file versions are kept until an admin purges them."
    : `Old file versions are purged after ${days} days.`;
}

function when(iso) {
  return iso ? new Date(iso).toLocaleString() : "never";
}

function Reference({ r }) {
  switch (r.kind) {
    case "data_file_version":
      return <>File version {r.version} of <strong>{r.filename}</strong> ({r.state})
        {r.dataset_id && <> in <Link to={`/datasets/${r.dataset_id}`}>dataset {r.dataset_id}</Link></>}</>;
    case "data_file":
      return <>File <strong>{r.filename}</strong> of dataset{" "}
        <Link to={`/datasets/${r.dataset_id}`}>{r.dataset}</Link></>;
    case "data_import":
      return <>Import {r.id} of <strong>{r.filename}</strong> into table {r.table} of the hosted database</>;
    case "release_part":
      return <>File <strong>{r.path}</strong> of release {r.release}</>;
    case "live_object":
      return <>File <strong>{r.filename}</strong> uploaded into table {r.table} of the hosted database
        (object {r.object_id})</>;
    default:
      return <>{r.kind} {r.id}</>;
  }
}

function Problem({ p }) {
  return (
    <li style={{ padding: "12px 0", borderTop: "1px solid #e5e7eb" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Badge color={STATUS_COLOR[p.status] || "gray"}>{STATUS_TEXT[p.status] || p.status}</Badge>
        <code style={{ fontSize: 12, wordBreak: "break-all" }}>{p.object_key}</code>
      </div>
      <div style={{ fontSize: 13, color: "#374151", marginTop: 4 }}>
        {p.project ? <>Project <Link to={`/projects/${p.project.id}`}>{p.project.name}</Link> · </> : null}
        {p.failing_since ? <>since {when(p.failing_since)} · </> : null}
        last checked {when(p.checked_at)}
      </div>
      {p.detail && <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2, wordBreak: "break-all" }}>{p.detail}</div>}
      {p.references.length > 0 && (
        <ul style={{ fontSize: 13, margin: "6px 0 0", paddingLeft: 18 }} aria-label={`Rows that name ${p.object_key}`}>
          {p.references.map(r => <li key={`${r.kind}-${r.id ?? r.object_id}`}><Reference r={r} /></li>)}
        </ul>
      )}
    </li>
  );
}

export default function StoredFilesCard() {
  const r = useApi("/admin/object-checks");
  const [rate, setRate] = useState(null);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (r.loading && !r.data) return <Loading />;
  if (r.error) return <ErrorNote error={r.error} />;
  const data = r.data;
  const form = rate ?? data.settings;
  const s = data.summary;

  const act = async (fn, message) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const out = await fn();
      setNote(message(out));
      r.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const save = e => {
    e.preventDefault();
    const body = { objects_per_run: Number(form.objects_per_run), mib_per_run: Number(form.mib_per_run) };
    if (form.retention_days !== undefined) body.retention_days = Number(form.retention_days);
    act(() => api.put("/admin/object-checks/settings", body), out => {
      setRate(null);
      const check = out.settings.objects_per_run === 0
        ? "Saved: the hourly check is off."
        : `Saved: each hourly run checks up to ${out.settings.objects_per_run} objects or ${out.settings.mib_per_run} MiB.`;
      return out.settings.retention_days === undefined ? check
        : `${check} ${retentionText(out.settings.retention_days)}`;
    });
  };
  const runNow = () => act(() => api.post("/admin/object-checks/run"), out => `Ran one batch: ${out.summary}.`);

  return (
    <>
      <Card>
        <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Stored files</h2>
        <ErrorNote error={error} />
        <SuccessNote>{note}</SuccessNote>
        <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 14px" }}>
          Every hour the portal re-reads a batch of the stored files (file versions, imported files,
          release files, files uploaded into a hosted database) and compares their size and SHA-256 with what it recorded at upload,
          oldest check first. A changed or missing file is listed below and written to the audit
          trail. Nothing is repaired: upload the right file again, or restore an older version.
        </p>
        <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "4px 14px", fontSize: 14, margin: "0 0 16px" }}>
          <dt>Stored objects</dt><dd style={{ margin: 0 }}>{s.objects}</dd>
          <dt>Never checked</dt><dd style={{ margin: 0 }}>{s.never_checked}</dd>
          <dt>OK at their last check</dt><dd style={{ margin: 0 }}>{s.ok}</dd>
          <dt>Changed or missing</dt><dd style={{ margin: 0 }}>{s.problems}</dd>
          {s.errors > 0 && <><dt>Not readable</dt><dd style={{ margin: 0 }}>{s.errors}</dd></>}
          <dt>Oldest check</dt><dd style={{ margin: 0 }}>{when(s.oldest_check)}</dd>
          <dt>Last check</dt><dd style={{ margin: 0 }}>{when(s.last_check)}</dd>
        </dl>
        <form onSubmit={save}>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <Field label="Objects per run" hint="0 switches the hourly check off.">
              <Input type="number" min={0} max={100000} required value={form.objects_per_run}
                     onChange={e => setRate({ ...form, objects_per_run: e.target.value })} />
            </Field>
            <Field label="MiB per run" hint={`At least one object is read per run. Recorded size: ${formatBytes(1024 * 1024 * Number(form.mib_per_run || 0))}.`}>
              <Input type="number" min={1} max={10000000} required value={form.mib_per_run}
                     onChange={e => setRate({ ...form, mib_per_run: e.target.value })} />
            </Field>
            {form.retention_days !== undefined && (
              <Field label="Keep old file versions (days)"
                     hint="A replaced or deleted file's old bytes that no frozen design version and no release names are purged once they are this old (checked daily). 0 keeps them until an admin purges them.">
                <Input type="number" min={0} max={36500} required value={form.retention_days}
                       onChange={e => setRate({ ...form, retention_days: e.target.value })} />
              </Field>
            )}
          </div>
          <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Btn type="submit" disabled={busy}>Save</Btn>
            <Btn variant="secondary" onClick={runNow} disabled={busy}>Check a batch now</Btn>
          </div>
        </form>
      </Card>
      <Card style={{ marginTop: 16 }}>
        <h2 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700 }}>Alerts</h2>
        {data.problems.length === 0
          ? <p style={{ fontSize: 14, color: "#374151", margin: 0 }}>No stored file differs from its checksum.</p>
          : <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>{data.problems.map(p => <Problem key={p.object_key} p={p} />)}</ul>}
      </Card>
    </>
  );
}
